import { useState, useMemo } from 'react';
import { useParams, useNavigate, NavLink } from 'react-router-dom';
import { ArrowLeft, Plus, FolderPlus, Search, X, Settings, Star, ChevronDown, ChevronRight } from 'lucide-react';
import { useTeamSpace } from '../../hooks/useTeamSpaces';
import { useDocumentTree, useDocumentFavorites, useCreateDocument } from '../../hooks/useDocuments';
import { useAuthStore } from '../../store/authStore';
import DocumentTreeNode from './DocumentTreeNode';
import TeamSpaceSettingsModal from './TeamSpaceSettingsModal';
import type { DocumentTreeItem } from '../../services/documentService';

// ─── CollapsibleSection ───
function CollapsibleSection({
  title,
  defaultOpen = true,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 px-2 py-1 w-full text-sm font-semibold text-gray-400 uppercase tracking-wider hover:text-gray-600 transition-colors"
      >
        {open ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
        {title}
      </button>
      {open && <div className="mt-1 space-y-0.5">{children}</div>}
    </div>
  );
}

// ─── 트리 검색 필터 ───
function filterTree(items: DocumentTreeItem[], query: string): DocumentTreeItem[] {
  const q = query.toLowerCase();
  return items.reduce<DocumentTreeItem[]>((acc, item) => {
    const childMatches = filterTree(item.children, query);
    if (item.title.toLowerCase().includes(q) || childMatches.length > 0) {
      acc.push({ ...item, children: childMatches.length > 0 ? childMatches : item.children });
    }
    return acc;
  }, []);
}

// CHG-130 REQ-DHR-027: scope prop 도입 + spaceId override (프로젝트 스코프에서 URL이 아니라 prop으로 주입)
interface SubSidebarProps {
  scope?: 'global' | 'project';
  overrideSpaceId?: string;
  projectName?: string;
}

export default function DocumentSubSidebar({ scope = 'global', overrideSpaceId, projectName }: SubSidebarProps = {}) {
  const params = useParams();
  const navigate = useNavigate();
  const currentUser = useAuthStore((s) => s.user);
  // scope='project'면 spaceId를 prop으로, 'global'은 URL params에서
  const spaceId = scope === 'project' ? overrideSpaceId : params.spaceId;
  const projectId = params.key; // URL의 :key (project route 내부)
  const isProjectScope = scope === 'project';

  const { data: teamSpace } = useTeamSpace(spaceId);
  const { data: documentTree } = useDocumentTree(spaceId);
  const { data: favorites } = useDocumentFavorites(spaceId);
  const createDoc = useCreateDocument();

  const [searchQuery, setSearchQuery] = useState('');
  const [showSettings, setShowSettings] = useState(false);

  const myRole = teamSpace?.members?.find((m) => m.user.id === currentUser?.id)?.role ?? null;

  const filteredTree = useMemo(() => {
    if (!documentTree) return [];
    if (!searchQuery.trim()) return documentTree;
    return filterTree(documentTree, searchQuery.trim());
  }, [documentTree, searchQuery]);

  const buildDocLink = (docId: string) =>
    isProjectScope
      ? `/projects/${projectId}/documents/${docId}`
      : `/documents/${spaceId}/${docId}`;

  const handleCreateDocument = () => {
    if (!spaceId) return;
    createDoc.mutate(
      { spaceId, data: { title: '새 문서' } },
      { onSuccess: (doc) => navigate(buildDocLink(doc.id)) },
    );
  };

  const handleCreateFolder = () => {
    if (!spaceId) return;
    createDoc.mutate({ spaceId, data: { title: '새 폴더', isFolder: true } });
  };

  // CHG-130 REQ-DHR-027: project scope에서는 카드 형태(전체 테두리+라운드)로 표시
  const asideClass = isProjectScope
    ? 'w-[280px] min-w-[280px] bg-white border border-gray-200 rounded-xl flex flex-col h-full overflow-hidden'
    : 'w-[280px] min-w-[280px] bg-white border-r border-gray-200 flex flex-col h-full';

  return (
    <aside className={asideClass}>
      {/* Header — 에디터 상단 바와 높이 정렬 (h-14) */}
      <div className="flex items-center gap-2 px-3 h-14 border-b border-gray-200 shrink-0">
        {/* CHG-130 REQ-DHR-027: scope='project'에서는 뒤로가기 버튼 숨김 */}
        {!isProjectScope && (
          <button
            onClick={() => navigate('/documents')}
            className="p-1 rounded hover:bg-gray-100"
            title="팀 스페이스 목록"
          >
            <ArrowLeft className="size-4 text-gray-500" />
          </button>
        )}
        <h2 className="text-sm font-semibold text-gray-900 truncate flex-1">
          {isProjectScope
            ? (projectName ? `${projectName} 문서함` : '프로젝트 문서함')
            : (teamSpace?.name ?? '로딩 중...')}
        </h2>
        {(myRole === 'OWNER' || myRole === 'MANAGER') && (
          <button onClick={() => setShowSettings(true)} className="p-1 rounded hover:bg-gray-100" title="설정">
            <Settings className="size-4 text-gray-400" />
          </button>
        )}
        <button onClick={handleCreateDocument} className="p-1 rounded hover:bg-gray-100" title="새 문서">
          <Plus className="size-4 text-gray-400" />
        </button>
      </div>

      {/* Search */}
      <div className="p-3 border-b border-gray-200 shrink-0">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Escape' && setSearchQuery('')}
            placeholder="문서 검색..."
            className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="size-4" />
            </button>
          )}
        </div>
      </div>

      {/* Document sections */}
      <div className="flex-1 overflow-y-auto px-2 py-3 space-y-4">
        {/* 즐겨찾기 */}
        <CollapsibleSection title="즐겨찾기">
          {favorites && favorites.length > 0 ? (
            favorites.map((fav) => (
              <NavLink
                key={fav.id}
                to={buildDocLink(fav.document.id)}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm transition-colors ${
                    isActive ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-600 hover:bg-gray-50'
                  }`
                }
              >
                <Star className="size-3.5 shrink-0 fill-yellow-400 text-yellow-400" />
                <span className="truncate">{fav.document.title}</span>
              </NavLink>
            ))
          ) : (
            <p className="px-3 py-2 text-sm text-gray-400">즐겨찾기한 문서가 없습니다.</p>
          )}
        </CollapsibleSection>

        {/* 문서 트리 */}
        <CollapsibleSection title="문서">
          {filteredTree.length > 0 ? (
            filteredTree.map((item) => (
              <DocumentTreeNode key={item.id} item={item} depth={0} spaceId={spaceId!} scope={scope} projectKey={projectId} />
            ))
          ) : (
            <p className="px-3 py-2 text-sm text-gray-400">문서가 없습니다.</p>
          )}
        </CollapsibleSection>
      </div>

      {/* Bottom */}
      <div className="p-3 border-t border-gray-200 shrink-0">
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={handleCreateDocument}
            disabled={createDoc.isPending}
            className="flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors disabled:opacity-50"
          >
            <Plus className="size-4" />
            새 문서
          </button>
          <button
            onClick={handleCreateFolder}
            disabled={createDoc.isPending}
            className="flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-yellow-700 bg-yellow-50 rounded-lg hover:bg-yellow-100 transition-colors disabled:opacity-50"
          >
            <FolderPlus className="size-4" />
            새 폴더
          </button>
        </div>
      </div>

      {/* Settings modal */}
      {spaceId && (
        <TeamSpaceSettingsModal
          spaceId={spaceId}
          open={showSettings}
          onClose={() => setShowSettings(false)}
          scope={scope}
          projectKey={projectId}
        />
      )}
    </aside>
  );
}
